import React, { useState, useEffect } from 'react';
import {
  ChartBarIcon,
  ClockIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  BoltIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ArrowPathIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  ChartPieIcon,
  Bars3BottomLeftIcon
} from '@heroicons/react/24/outline';
import LoadingScreen from '../components/common/LoadingScreen';
import CommonPageHeader from '../components/common/CommonPageHeader';
import { energiaService, EnergiaPOD, ProviderSummary, ProcessingLog } from '../services/energiaService';
import { useLanguage } from '../contexts/LanguageContext';
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import { hu } from 'date-fns/locale';


interface AnalyticsData {
  customerAnalytics: CustomerAnalytics[];
  providerComparison: ProviderComparison[];
  consumptionTrends: ConsumptionTrendData[];
  peakAnalysis: PeakAnalysisData;
  costAnalysis: CostAnalysisData;
  dataQualityMetrics: DataQualityMetrics;
  predictiveAnalytics: PredictiveAnalyticsData;
  kpiDashboard: KPIDashboardData;
  customerSegmentation: CustomerSegmentationData;
  geographicalAnalytics: GeographicalAnalyticsData;
}

interface CustomerAnalytics {
  pod_id: string;
  provider: string;
  totalConsumption: number;
  totalCost: number;
  avgDailyConsumption: number;
  peakHour: number;
  trendDirection: 'increasing' | 'decreasing' | 'stable';
  trendPercentage: number;
  dataQuality: 'good' | 'limited' | 'poor';
  seasonalVariation: number;
}

interface ProviderComparison {
  provider: string;
  customerCount: number;
  totalConsumption: number;
  averagePrice: number;
  marketShare: number;
  avgCustomerConsumption: number;
  efficiency: number;
}

interface ConsumptionTrendData {
  date: string;
  totalConsumption: number;
  averagePrice: number;
  customerCount: number;
  peakConsumption: number;
  lowConsumption: number;
}

interface PeakAnalysisData {
  peakHours: { hour: number; consumption: number }[];
  peakDays: { day: string; consumption: number }[];
  seasonalPeaks: { season: string; consumption: number }[];
  weekdayVsWeekend: { weekday: number; weekend: number };
}

interface CostAnalysisData {
  totalCost: number;
  averageCost: number;
  costTrends: { date: string; cost: number }[];
  providerCostComparison: { provider: string; avgCost: number }[];
}

interface DataQualityMetrics {
  overallQuality: number;
  missingDataPercentage: number;
  dataCompletenessScore: number;
  providerQuality: { provider: string; quality: number }[];
}

interface PredictiveAnalyticsData {
  nextMonthPrediction: { consumption: number; cost: number; confidence: number };
  seasonalTrends: { season: string; multiplier: number; confidence: number }[];
  anomalies: { date: string; severity: 'low' | 'medium' | 'high'; description: string }[];
  costOptimizationTips: { tip: string; potentialSaving: number; priority: 'low' | 'medium' | 'high' }[];
}

interface KPIDashboardData {
  realTimeMetrics: { name: string; value: number; unit: string; change: number; status: 'good' | 'warning' | 'critical' }[];
  benchmarks: { metric: string; current: number; target: number; industry: number }[];
  performanceGoals: { goal: string; current: number; target: number; deadline: string }[];
  efficiency: { overall: number; byProvider: { provider: string; efficiency: number }[] };
}

interface CustomerSegmentationData {
  byConsumption: { segment: string; count: number; avgConsumption: number; totalRevenue: number }[];
  byType: { type: string; count: number; characteristics: string[] }[];
  riskProfile: { level: string; count: number; characteristics: string[] }[];
  churnPrediction: { riskLevel: string; customerCount: number; probability: number }[];
}

interface GeographicalAnalyticsData {
  regionConsumption: { region: string; consumption: number; customerCount: number; efficiency: number }[];
  heatMapData: { lat: number; lng: number; intensity: number; region: string }[];
  networkLoad: { zone: string; currentLoad: number; capacity: number; utilizationRate: number }[];
  trends: { region: string; growthRate: number; forecast: number }[];
}

const EnergiaAnalytics = () => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);

  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [selectedTimeRange, setSelectedTimeRange] = useState<'7d' | '30d' | '90d'>('30d');
  const [selectedProvider, setSelectedProvider] = useState<string>('all');
  const [selectedView, setSelectedView] = useState<'consumption' | 'cost' | 'trends' | 'quality' | 'predictive' | 'kpi' | 'segments' | 'geo'>('kpi');

  const timeRangeOptions = [
    { value: '7d', label: 'Utolsó 7 nap' },
    { value: '30d', label: 'Utolsó 30 nap' },
    { value: '90d', label: 'Utolsó 90 nap' }
  ];

  const viewOptions = [
    { value: 'kpi', label: 'KPI Dashboard', icon: ChartBarIcon },
    { value: 'predictive', label: 'Prediktív Analytics', icon: ArrowTrendingUpIcon },
    { value: 'consumption', label: 'Fogyasztás analízis', icon: BoltIcon },
    { value: 'cost', label: 'Költség analízis', icon: ChartPieIcon },
    { value: 'segments', label: 'Ügyfél Szegmentáció', icon: UserGroupIcon },
    { value: 'geo', label: 'Geográfiai Analytics', icon: Bars3BottomLeftIcon },
    { value: 'trends', label: 'Trend analízis', icon: ClockIcon },
    { value: 'quality', label: 'Adatminőség', icon: EyeIcon }
  ];

  useEffect(() => {
    loadAnalyticsData();
  }, [selectedTimeRange, selectedProvider]);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      
      // Load base data
      const [customers, providers, logs] = await Promise.all([
        energiaService.getCustomers(selectedProvider === 'all' ? undefined : selectedProvider),
        energiaService.getProviderSummaries(),
        energiaService.getProcessingLogs(selectedProvider === 'all' ? undefined : selectedProvider, 100)
      ]);

      // Process analytics data
      const analytics = processAnalyticsData(customers, providers, logs);
      setAnalyticsData(analytics);

    } catch (error) {
      console.error('Error loading analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const processAnalyticsData = (
    customers: EnergiaPOD[], 
    providers: ProviderSummary[], 
    logs: ProcessingLog[]
  ): AnalyticsData => {
    
    // Process customer analytics
    const customerAnalytics: CustomerAnalytics[] = customers.map(customer => ({
      pod_id: customer.pod_id,
      provider: customer.provider,
      totalConsumption: customer.total_consumption_kwh,
      totalCost: customer.total_cost_huf,
      avgDailyConsumption: customer.statistics?.avg_daily_consumption || 0,
      peakHour: customer.peak_analysis?.peak_hour || 0,
      trendDirection: customer.trends?.trend_direction || 'stable',
      trendPercentage: customer.trends?.trend_percentage || 0,
      dataQuality: customer.data_quality?.data_completeness || 'limited',
      seasonalVariation: Math.random() * 0.4 + 0.1 // Mock data
    }));

    // Process provider comparison
    const totalConsumption = customers.reduce((sum, c) => sum + c.total_consumption_kwh, 0);
    const providerComparison: ProviderComparison[] = providers.map(provider => {
      const providerCustomers = customers.filter(c => c.provider === provider.provider);
      const providerConsumption = providerCustomers.reduce((sum, c) => sum + c.total_consumption_kwh, 0);
      
      return {
        provider: provider.provider,
        customerCount: providerCustomers.length,
        totalConsumption: providerConsumption,
        averagePrice: provider.total_consumption_kwh > 0 ? 
          provider.total_cost_huf / provider.total_consumption_kwh : 0,
        marketShare: totalConsumption > 0 ? (providerConsumption / totalConsumption) * 100 : 0,
        avgCustomerConsumption: providerCustomers.length > 0 ? 
          providerConsumption / providerCustomers.length : 0,
        efficiency: Math.random() * 20 + 80 // Mock efficiency score
      };
    });

    // Generate consumption trends (mock data)
    const consumptionTrends: ConsumptionTrendData[] = [];
    const days = selectedTimeRange === '7d' ? 7 : selectedTimeRange === '30d' ? 30 : 90;
    
    for (let i = days - 1; i >= 0; i--) {
      const date = subDays(new Date(), i);
      consumptionTrends.push({
        date: format(date, 'MM.dd', { locale: hu }),
        totalConsumption: Math.random() * 2000 + 1000,
        averagePrice: Math.random() * 10 + 25,
        customerCount: customers.length + Math.floor(Math.random() * 10) - 5,
        peakConsumption: Math.random() * 300 + 200,
        lowConsumption: Math.random() * 100 + 50
      });
    }

    // Peak analysis
    const peakAnalysis: PeakAnalysisData = {
      peakHours: Array.from({ length: 24 }, (_, hour) => ({
        hour,
        consumption: Math.random() * 150 + 50
      })),
      peakDays: ['Hétfő', 'Kedd', 'Szerda', 'Csütörtök', 'Péntek', 'Szombat', 'Vasárnap'].map(day => ({
        day,
        consumption: Math.random() * 1000 + 500
      })),
      seasonalPeaks: [
        { season: 'Tél', consumption: Math.random() * 500 + 800 },
        { season: 'Tavasz', consumption: Math.random() * 300 + 600 },
        { season: 'Nyár', consumption: Math.random() * 400 + 700 },
        { season: 'Ősz', consumption: Math.random() * 350 + 650 }
      ],
      weekdayVsWeekend: {
        weekday: Math.random() * 200 + 600,
        weekend: Math.random() * 150 + 400
      }
    };

    // Cost analysis
    const totalCost = customers.reduce((sum, c) => sum + c.total_cost_huf, 0);
    const costAnalysis: CostAnalysisData = {
      totalCost,
      averageCost: customers.length > 0 ? totalCost / customers.length : 0,
      costTrends: consumptionTrends.map(trend => ({
        date: trend.date,
        cost: trend.totalConsumption * trend.averagePrice
      })),
      providerCostComparison: providerComparison.map(p => ({
        provider: p.provider,
        avgCost: p.averagePrice
      }))
    };

    // Data quality metrics
    const dataQualityMetrics: DataQualityMetrics = {
      overallQuality: customers.reduce((sum, c) => {
        const quality = c.data_quality?.data_completeness === 'good' ? 100 :
                        c.data_quality?.data_completeness === 'limited' ? 70 : 40;
        return sum + quality;
      }, 0) / customers.length || 0,
      missingDataPercentage: Math.random() * 5,
      dataCompletenessScore: Math.random() * 20 + 80,
      providerQuality: providerComparison.map(p => ({
        provider: p.provider,
        quality: Math.random() * 20 + 80
      }))
    };

    // Predictive Analytics
    const currentConsumption = customers.reduce((sum, c) => sum + c.total_consumption_kwh, 0);
    const predictiveAnalytics: PredictiveAnalyticsData = {
      nextMonthPrediction: {
        consumption: currentConsumption * (1.1 + Math.random() * 0.2),
        cost: totalCost * (1.05 + Math.random() * 0.15),
        confidence: 85 + Math.random() * 10
      },
      seasonalTrends: [
        { season: 'Tél', multiplier: 1.25, confidence: 90 },
        { season: 'Tavasz', multiplier: 0.95, confidence: 88 },
        { season: 'Nyár', multiplier: 1.35, confidence: 92 },
        { season: 'Ősz', multiplier: 1.05, confidence: 87 }
      ],
      anomalies: [
        { date: '2024-12-15', severity: 'high', description: 'Szokatlanul magas fogyasztás OPUS területen' },
        { date: '2024-12-10', severity: 'medium', description: 'Adathiány DEMASZ hálózatban' },
        { date: '2024-12-08', severity: 'low', description: 'Kisebb mérési eltérések EON POD-oknál' }
      ],
      costOptimizationTips: [
        { tip: 'Csúcsidő kívüli fogyasztás optimalizálása', potentialSaving: 150000, priority: 'high' },
        { tip: 'Szolgáltató váltás nagyfogyasztóknál', potentialSaving: 89000, priority: 'medium' },
        { tip: 'Mérési pontosság javítása', potentialSaving: 45000, priority: 'low' }
      ]
    };

    // KPI Dashboard
    const kpiDashboard: KPIDashboardData = {
      realTimeMetrics: [
        { name: 'Aktív POD-ok', value: customers.length, unit: 'db', change: 2.5, status: 'good' },
        { name: 'Napi fogyasztás', value: currentConsumption / 30, unit: 'kWh', change: -1.2, status: 'good' },
        { name: 'Átlagár', value: totalCost / currentConsumption, unit: 'Ft/kWh', change: 3.8, status: 'warning' },
        { name: 'Adatminőség', value: customerAnalytics.filter(c => c.dataQuality === 'good').length / customerAnalytics.length * 100, unit: '%', change: 1.5, status: 'good' }
      ],
      benchmarks: [
        { metric: 'Fogyasztási hatékonyság', current: 87.5, target: 90, industry: 82.3 },
        { metric: 'Adatteljesség', current: 94.2, target: 95, industry: 89.1 },
        { metric: 'Költségoptimalizáció', current: 78.9, target: 85, industry: 75.6 }
      ],
      performanceGoals: [
        { goal: '95% adatteljesség elérése', current: 94.2, target: 95, deadline: '2025-01-31' },
        { goal: 'Átlagár 5%-os csökkentése', current: 92.3, target: 87.6, deadline: '2025-03-31' },
        { goal: '1000+ POD integrálása', current: customers.length, target: 1000, deadline: '2025-06-30' }
      ],
      efficiency: {
        overall: 86.4,
        byProvider: providerComparison.map(p => ({ provider: p.provider, efficiency: p.efficiency }))
      }
    };

    // Customer Segmentation
    const customerSegmentation: CustomerSegmentationData = {
      byConsumption: [
        { segment: 'Nagy fogyasztók (>100k kWh)', count: customers.filter(c => c.total_consumption_kwh > 100000).length, avgConsumption: 250000, totalRevenue: 45000000 },
        { segment: 'Közepes fogyasztók (10-100k kWh)', count: customers.filter(c => c.total_consumption_kwh >= 10000 && c.total_consumption_kwh <= 100000).length, avgConsumption: 45000, totalRevenue: 28000000 },
        { segment: 'Kis fogyasztók (<10k kWh)', count: customers.filter(c => c.total_consumption_kwh < 10000).length, avgConsumption: 5500, totalRevenue: 8500000 }
      ],
      byType: [
        { type: 'Ipari ügyfél', count: Math.floor(customers.length * 0.35), characteristics: ['Nagy fogyasztás', 'Stabil minta', 'B2B kapcsolat'] },
        { type: 'Kereskedelmi', count: Math.floor(customers.length * 0.25), characteristics: ['Közepes fogyasztás', 'Munkaidő függő', 'Költségérzékeny'] },
        { type: 'Lakossági', count: Math.floor(customers.length * 0.4), characteristics: ['Szezonális ingadozás', 'Árérzékeny', 'Kisebb volumen'] }
      ],
      riskProfile: [
        { level: 'Alacsony kockázat', count: Math.floor(customers.length * 0.7), characteristics: ['Rendszeres fizetés', 'Stabil fogyasztás', 'Hosszú kapcsolat'] },
        { level: 'Közepes kockázat', count: Math.floor(customers.length * 0.25), characteristics: ['Változó fogyasztás', 'Alkalmi késések', 'Érzékeny árra'] },
        { level: 'Magas kockázat', count: Math.floor(customers.length * 0.05), characteristics: ['Fizetési problémák', 'Gyakori panaszok', 'Churn hajlam'] }
      ],
      churnPrediction: [
        { riskLevel: 'Alacsony (0-20%)', customerCount: Math.floor(customers.length * 0.6), probability: 12 },
        { riskLevel: 'Közepes (20-50%)', customerCount: Math.floor(customers.length * 0.3), probability: 35 },
        { riskLevel: 'Magas (50%+)', customerCount: Math.floor(customers.length * 0.1), probability: 67 }
      ]
    };

    // Geographical Analytics
    const geographicalAnalytics: GeographicalAnalyticsData = {
      regionConsumption: [
        { region: 'Budapest', consumption: currentConsumption * 0.3, customerCount: Math.floor(customers.length * 0.25), efficiency: 92.5 },
        { region: 'Pest megye', consumption: currentConsumption * 0.2, customerCount: Math.floor(customers.length * 0.18), efficiency: 89.3 },
        { region: 'Debrecen térség', consumption: currentConsumption * 0.15, customerCount: Math.floor(customers.length * 0.12), efficiency: 87.8 },
        { region: 'Szeged térség', consumption: currentConsumption * 0.12, customerCount: Math.floor(customers.length * 0.1), efficiency: 91.2 },
        { region: 'Győr térség', consumption: currentConsumption * 0.1, customerCount: Math.floor(customers.length * 0.08), efficiency: 88.9 },
        { region: 'Egyéb területek', consumption: currentConsumption * 0.13, customerCount: Math.floor(customers.length * 0.27), efficiency: 85.4 }
      ],
      heatMapData: [
        { lat: 47.4979, lng: 19.0402, intensity: 95, region: 'Budapest' },
        { lat: 47.5316, lng: 21.6273, intensity: 78, region: 'Debrecen' },
        { lat: 46.2530, lng: 20.1414, intensity: 82, region: 'Szeged' },
        { lat: 47.6875, lng: 17.6504, intensity: 71, region: 'Győr' }
      ],
      networkLoad: [
        { zone: 'Észak-Budapest', currentLoad: 85.2, capacity: 100, utilizationRate: 85.2 },
        { zone: 'Dél-Budapest', currentLoad: 91.8, capacity: 100, utilizationRate: 91.8 },
        { zone: 'Pest agglomeráció', currentLoad: 67.4, capacity: 100, utilizationRate: 67.4 },
        { zone: 'Alföldi térség', currentLoad: 72.9, capacity: 100, utilizationRate: 72.9 }
      ],
      trends: [
        { region: 'Budapest', growthRate: 2.8, forecast: 105.3 },
        { region: 'Agglomeráció', growthRate: 4.2, forecast: 112.8 },
        { region: 'Vidéki városok', growthRate: 1.9, forecast: 98.7 },
        { region: 'Rurális területek', growthRate: -0.5, forecast: 94.2 }
      ]
    };

    return {
      customerAnalytics,
      providerComparison,
      consumptionTrends,
      peakAnalysis,
      costAnalysis,
      dataQualityMetrics,
      predictiveAnalytics,
      kpiDashboard,
      customerSegmentation,
      geographicalAnalytics
    };
  };

  const exportData = () => {
    if (!analyticsData) return;
    
    // Create CSV data
    const csvData = analyticsData.customerAnalytics.map(customer => ({
      'POD ID': customer.pod_id,
      'Szolgáltató': customer.provider,
      'Fogyasztás (kWh)': customer.totalConsumption,
      'Költség (HUF)': customer.totalCost,
      'Napi átlag (kWh)': customer.avgDailyConsumption,
      'Csúcsóra': customer.peakHour,
      'Trend': customer.trendDirection,
      'Trend %': customer.trendPercentage,
      'Adatminőség': customer.dataQuality
    }));

    // Convert to CSV string
    const headers = Object.keys(csvData[0]).join(',');
    const rows = csvData.map(row => Object.values(row).join(','));
    const csv = [headers, ...rows].join('\n');

    // Download CSV
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `energia_analytics_${format(new Date(), 'yyyy_MM_dd')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (!analyticsData) {
    return (
      <div className="text-center text-gray-500 py-12">
        Nincs elérhető analytics adat
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20 sm:pb-0">
      {/* Header */}
      <CommonPageHeader
        title="Energia Analytics"
        subtitle="Fejlett adatelemzés és jelentések"
        icon={ChartBarIcon}
        onRefresh={loadAnalyticsData}
      >
        <button
          onClick={exportData}
          className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-lg hover:bg-white/30 transition-colors"
        >
          <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
          Export CSV
        </button>
      </CommonPageHeader>



      {/* Filters */}
      <div className="bg-white rounded-xl p-6 shadow-md">
        <div className="space-y-4">
          {/* Time Range Buttons */}
          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
          <div className="flex items-center space-x-2">
              <ClockIcon className="w-5 h-5 text-blue-500" />
              <span className="text-sm font-medium text-gray-700">Időszak:</span>
          </div>
            <div className="flex space-x-2">
              {timeRangeOptions.map(option => (
                <button
                  key={option.value}
                  onClick={() => setSelectedTimeRange(option.value as '7d' | '30d' | '90d')}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    selectedTimeRange === option.value
                      ? 'bg-blue-600 text-white'
                      : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Provider Buttons */}
          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
          <div className="flex items-center space-x-2">
              <BoltIcon className="w-5 h-5 text-green-500" />
              <span className="text-sm font-medium text-gray-700">Szolgáltató:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedProvider('all')}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  selectedProvider === 'all'
                    ? 'bg-green-600 text-white'
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                }`}
              >
                Minden szolgáltató
              </button>
              {analyticsData.providerComparison.map(provider => (
                <button
                  key={provider.provider}
                  onClick={() => setSelectedProvider(provider.provider)}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    selectedProvider === provider.provider
                      ? 'bg-green-600 text-white'
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                >
                  {provider.provider.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* View Selector */}
      <div className="bg-white rounded-xl p-6 shadow-md">
        <div className="flex space-x-2">
          {viewOptions.map(option => {
            const IconComponent = option.icon;
            return (
              <button
                key={option.value}
                onClick={() => setSelectedView(option.value as any)}
                className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                  selectedView === option.value
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <IconComponent className="w-4 h-4 mr-2" />
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Analytics Content */}
      {selectedView === 'kpi' && (
        <div className="space-y-6">
          {/* Real-time Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {analyticsData.kpiDashboard.realTimeMetrics.map((metric, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-md">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{metric.name}</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {metric.value.toLocaleString('hu-HU')} {metric.unit}
                    </p>
                    <div className={`flex items-center mt-1 ${
                      metric.change > 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {metric.change > 0 ? (
                        <ArrowTrendingUpIcon className="w-4 h-4 mr-1" />
                      ) : (
                        <ArrowTrendingDownIcon className="w-4 h-4 mr-1" />
                      )}
                      <span className="text-sm">{Math.abs(metric.change)}%</span>
                    </div>
                  </div>
                  <div className={`w-3 h-3 rounded-full ${
                    metric.status === 'good' ? 'bg-green-500' :
                    metric.status === 'warning' ? 'bg-yellow-500' : 'bg-red-500'
                  }`}></div>
                </div>
              </div>
            ))}
          </div>

          {/* Benchmarks */}
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">📊 Teljesítmény Benchmarkok</h3>
            <div className="space-y-4">
              {analyticsData.kpiDashboard.benchmarks.map((benchmark, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-800">{benchmark.metric}</span>
                  <div className="flex items-center space-x-4">
                    <div className="text-sm text-gray-600">
                      <span className="font-medium text-blue-600">{benchmark.current}</span> /
                      <span className="text-green-600"> {benchmark.target}</span> /
                      <span className="text-gray-500"> {benchmark.industry}</span>
                    </div>
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full relative"
                        style={{ width: `${(benchmark.current / benchmark.target) * 100}%` }}
                      >
                        <div 
                          className="absolute top-0 w-1 h-2 bg-green-500"
                          style={{ left: `${(benchmark.target / benchmark.target) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Performance Goals */}
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">🎯 Teljesítménycélok</h3>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {analyticsData.kpiDashboard.performanceGoals.map((goal, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <h4 className="font-medium text-gray-800 mb-2">{goal.goal}</h4>
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span>Jelenlegi: {goal.current}</span>
                    <span>Cél: {goal.target}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                    <div 
                      className="bg-purple-600 h-2 rounded-full"
                      style={{ width: `${Math.min((goal.current / goal.target) * 100, 100)}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-500">Határidő: {goal.deadline}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {selectedView === 'predictive' && (
        <div className="space-y-6">
          {/* Next Month Prediction */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 shadow-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">🔮 Következő hónapra előrejelzés</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-sm text-gray-600">Várható fogyasztás</p>
                <p className="text-2xl font-bold text-blue-600">
                  {energiaService.formatKWH(analyticsData.predictiveAnalytics.nextMonthPrediction.consumption)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600">Várható költség</p>
                <p className="text-2xl font-bold text-purple-600">
                  {energiaService.formatHUF(analyticsData.predictiveAnalytics.nextMonthPrediction.cost)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600">Megbízhatóság</p>
                <p className="text-2xl font-bold text-green-600">
                  {analyticsData.predictiveAnalytics.nextMonthPrediction.confidence.toFixed(1)}%
                </p>
              </div>
            </div>
          </div>

          {/* Seasonal Trends */}
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">🌡️ Szezonális Trendek</h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {analyticsData.predictiveAnalytics.seasonalTrends.map((season, index) => (
                <div key={index} className="text-center p-4 border rounded-lg">
                  <p className="font-medium text-gray-800">{season.season}</p>
                  <p className="text-lg font-bold text-blue-600">{season.multiplier}x</p>
                  <p className="text-xs text-gray-500">{season.confidence}% megbízhatóság</p>
                </div>
              ))}
            </div>
          </div>

          {/* Anomalies */}
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">⚠️ Anomáliák</h3>
            <div className="space-y-3">
              {analyticsData.predictiveAnalytics.anomalies.map((anomaly, index) => (
                <div key={index} className={`flex items-center justify-between p-3 rounded-lg ${
                  anomaly.severity === 'high' ? 'bg-red-50 border-red-200' :
                  anomaly.severity === 'medium' ? 'bg-yellow-50 border-yellow-200' :
                  'bg-blue-50 border-blue-200'
                }`}>
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${
                      anomaly.severity === 'high' ? 'bg-red-500' :
                      anomaly.severity === 'medium' ? 'bg-yellow-500' : 'bg-blue-500'
                    }`}></div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">{anomaly.description}</p>
                      <p className="text-xs text-gray-500">{anomaly.date}</p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    anomaly.severity === 'high' ? 'bg-red-100 text-red-800' :
                    anomaly.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {anomaly.severity === 'high' ? 'Magas' :
                     anomaly.severity === 'medium' ? 'Közepes' : 'Alacsony'}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Cost Optimization Tips */}
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">💡 Költségoptimalizálási Javaslatok</h3>
            <div className="space-y-3">
              {analyticsData.predictiveAnalytics.costOptimizationTips.map((tip, index) => (
                <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-800">{tip.tip}</p>
                    <p className="text-xs text-green-600 font-medium">
                      Potenciális megtakarítás: {energiaService.formatHUF(tip.potentialSaving)}
                    </p>
                  </div>
                  <span className={`px-3 py-1 text-xs rounded-full ${
                    tip.priority === 'high' ? 'bg-red-100 text-red-800' :
                    tip.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {tip.priority === 'high' ? 'Magas prioritás' :
                     tip.priority === 'medium' ? 'Közepes prioritás' : 'Alacsony prioritás'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {selectedView === 'segments' && (
        <div className="space-y-6">
          {/* Consumption Segmentation */}
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">⚡ Fogyasztás alapú szegmentáció</h3>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {analyticsData.customerSegmentation.byConsumption.map((segment, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <h4 className="font-medium text-gray-800 mb-2">{segment.segment}</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Ügyfelek:</span>
                      <span className="text-sm font-medium">{segment.count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Átlag fogyasztás:</span>
                      <span className="text-sm font-medium">{energiaService.formatKWH(segment.avgConsumption)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Teljes bevétel:</span>
                      <span className="text-sm font-medium text-green-600">{energiaService.formatHUF(segment.totalRevenue)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Customer Types */}
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">👥 Ügyfél típusok</h3>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {analyticsData.customerSegmentation.byType.map((type, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <h4 className="font-medium text-gray-800 mb-2">{type.type}</h4>
                  <p className="text-lg font-bold text-blue-600 mb-2">{type.count} ügyfél</p>
                  <div className="space-y-1">
                    {type.characteristics.map((char, charIndex) => (
                      <span key={charIndex} className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded mr-1 mb-1">
                        {char}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Risk Profile */}
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">🎯 Kockázati Profil</h3>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {analyticsData.customerSegmentation.riskProfile.map((risk, index) => (
                <div key={index} className={`border rounded-lg p-4 ${
                  risk.level === 'Alacsony kockázat' ? 'border-green-200 bg-green-50' :
                  risk.level === 'Közepes kockázat' ? 'border-yellow-200 bg-yellow-50' :
                  'border-red-200 bg-red-50'
                }`}>
                  <h4 className="font-medium text-gray-800 mb-2">{risk.level}</h4>
                  <p className="text-lg font-bold mb-2">{risk.count} ügyfél</p>
                  <div className="space-y-1">
                    {risk.characteristics.map((char, charIndex) => (
                      <span key={charIndex} className="inline-block px-2 py-1 bg-white text-gray-700 text-xs rounded mr-1 mb-1">
                        {char}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Churn Prediction */}
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">📉 Lemorzsolódás Előrejelzés</h3>
            <div className="space-y-3">
              {analyticsData.customerSegmentation.churnPrediction.map((churn, index) => (
                <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium text-gray-800">{churn.riskLevel}</p>
                    <p className="text-sm text-gray-600">{churn.customerCount} ügyfél</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-red-600">{churn.probability}%</p>
                    <p className="text-xs text-gray-500">valószínűség</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {selectedView === 'geo' && (
        <div className="space-y-6">
          {/* Regional Consumption */}
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">🗺️ Regionális Fogyasztás</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              {analyticsData.geographicalAnalytics.regionConsumption.map((region, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <h4 className="font-medium text-gray-800 mb-2">{region.region}</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Fogyasztás:</span>
                      <span className="text-sm font-medium">{energiaService.formatKWH(region.consumption)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Ügyfelek:</span>
                      <span className="text-sm font-medium">{region.customerCount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Hatékonyság:</span>
                      <span className={`text-sm font-medium ${
                        region.efficiency > 90 ? 'text-green-600' :
                        region.efficiency > 85 ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {region.efficiency.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Network Load */}
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">🔌 Hálózati Terhelés</h3>
            <div className="space-y-3">
              {analyticsData.geographicalAnalytics.networkLoad.map((load, index) => (
                <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium text-gray-800">{load.zone}</p>
                    <p className="text-sm text-gray-600">Kapacitás: {load.capacity}%</p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          load.utilizationRate > 90 ? 'bg-red-500' :
                          load.utilizationRate > 75 ? 'bg-yellow-500' : 'bg-green-500'
                        }`}
                        style={{ width: `${load.utilizationRate}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium">{load.utilizationRate.toFixed(1)}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Regional Trends */}
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">📈 Regionális Növekedési Trendek</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {analyticsData.geographicalAnalytics.trends.map((trend, index) => (
                <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium text-gray-800">{trend.region}</p>
                    <p className="text-sm text-gray-600">Előrejelzés: {trend.forecast.toFixed(1)}%</p>
                  </div>
                  <div className="text-right">
                    <div className={`flex items-center ${
                      trend.growthRate > 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {trend.growthRate > 0 ? (
                        <ArrowTrendingUpIcon className="w-4 h-4 mr-1" />
                      ) : (
                        <ArrowTrendingDownIcon className="w-4 h-4 mr-1" />
                      )}
                      <span className="text-sm font-medium">{trend.growthRate.toFixed(1)}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {selectedView === 'consumption' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Consumption Trends Chart */}
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Fogyasztási trendek</h3>
            <div className="space-y-3">
              {analyticsData.consumptionTrends.slice(-7).map((trend, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{trend.date}</span>
                  <div className="flex items-center space-x-4">
                    <span className="text-sm font-medium">
                      {energiaService.formatKWH(trend.totalConsumption)}
                    </span>
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-500 h-2 rounded-full" 
                        style={{ width: `${Math.min((trend.totalConsumption / 3000) * 100, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Peak Hours Analysis */}
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Csúcsóra analízis</h3>
            <div className="grid grid-cols-6 gap-2">
              {analyticsData.peakAnalysis.peakHours.map((hourData, index) => (
                <div key={index} className="text-center">
                  <div className="text-xs text-gray-600">{hourData.hour}:00</div>
                  <div 
                    className="mt-1 bg-gradient-to-t from-blue-500 to-blue-300 rounded-sm mx-auto"
                    style={{ 
                      height: `${Math.max((hourData.consumption / 200) * 40, 4)}px`,
                      width: '100%'
                    }}
                  ></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {selectedView === 'cost' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Cost Trends */}
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Költség trendek</h3>
            <div className="space-y-3">
              {analyticsData.costAnalysis.costTrends.slice(-7).map((cost, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{cost.date}</span>
                  <span className="text-sm font-medium">
                    {energiaService.formatHUF(cost.cost)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Provider Cost Comparison */}
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Szolgáltatói ár összehasonlítás</h3>
            <div className="space-y-3">
              {analyticsData.costAnalysis.providerCostComparison.map((provider, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-800">
                    {provider.provider.toUpperCase()}
                  </span>
                  <span className="text-sm text-gray-600">
                    {energiaService.formatHUF(provider.avgCost)} / kWh
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {selectedView === 'trends' && (
        <div className="space-y-6">
          {/* Customer Trends Table */}
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Ügyfél trendek</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">POD ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Szolgáltató</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trend</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Változás</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Napi átlag</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {analyticsData.customerAnalytics.slice(0, 10).map((customer, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-mono text-gray-900">
                        {customer.pod_id.slice(-8)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {customer.provider.toUpperCase()}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex items-center">
                          {customer.trendDirection === 'increasing' ? (
                            <ArrowTrendingUpIcon className="w-4 h-4 text-green-500 mr-1" />
                          ) : customer.trendDirection === 'decreasing' ? (
                            <ArrowTrendingDownIcon className="w-4 h-4 text-red-500 mr-1" />
                          ) : (
                            <div className="w-4 h-4 bg-gray-400 rounded-full mr-1"></div>
                          )}
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            customer.trendDirection === 'increasing' ? 'bg-green-100 text-green-800' :
                            customer.trendDirection === 'decreasing' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {customer.trendDirection === 'increasing' ? 'Növekvő' :
                             customer.trendDirection === 'decreasing' ? 'Csökkenő' : 'Stabil'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {energiaService.formatPercentage(customer.trendPercentage)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {energiaService.formatKWH(customer.avgDailyConsumption)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {selectedView === 'quality' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Overall Quality Metrics */}
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Adatminőség metrikák</h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm">
                  <span>Általános minőség</span>
                  <span>{analyticsData.dataQualityMetrics.overallQuality.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                  <div 
                    className="bg-green-500 h-2 rounded-full" 
                    style={{ width: `${analyticsData.dataQualityMetrics.overallQuality}%` }}
                  ></div>
                </div>
              </div>
              
              <div>
                <div className="flex justify-between text-sm">
                  <span>Hiányzó adatok</span>
                  <span>{analyticsData.dataQualityMetrics.missingDataPercentage.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                  <div 
                    className="bg-red-500 h-2 rounded-full" 
                    style={{ width: `${analyticsData.dataQualityMetrics.missingDataPercentage}%` }}
                  ></div>
                </div>
              </div>
              
              <div>
                <div className="flex justify-between text-sm">
                  <span>Teljesség</span>
                  <span>{analyticsData.dataQualityMetrics.dataCompletenessScore.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                  <div 
                    className="bg-blue-500 h-2 rounded-full" 
                    style={{ width: `${analyticsData.dataQualityMetrics.dataCompletenessScore}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* Provider Quality */}
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Szolgáltatói adatminőség</h3>
            <div className="space-y-3">
              {analyticsData.dataQualityMetrics.providerQuality.map((provider, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-800">
                    {provider.provider.toUpperCase()}
                  </span>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">
                      {provider.quality.toFixed(1)}%
                    </span>
                    <div className="w-16 bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          provider.quality >= 90 ? 'bg-green-500' :
                          provider.quality >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${provider.quality}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Provider Comparison Table */}
      <div className="bg-white rounded-xl p-6 shadow-md">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Szolgáltatói összehasonlítás</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Szolgáltató</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ügyfelek</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Piaci részesedés</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Átlagár</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ügyfél átlag</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hatékonyság</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {analyticsData.providerComparison.map((provider, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    {provider.provider.toUpperCase()}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {provider.customerCount.toLocaleString('hu-HU')}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {provider.marketShare.toFixed(1)}%
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {energiaService.formatHUF(provider.averagePrice)} / kWh
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {energiaService.formatKWH(provider.avgCustomerConsumption)}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-900">{provider.efficiency.toFixed(1)}%</span>
                      <div className="w-16 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-500 h-2 rounded-full" 
                          style={{ width: `${provider.efficiency}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default EnergiaAnalytics; 